import { logger } from 'utils/logger';
import { ConfigContext } from "App";
import { getEffectiveUser } from "helpers/impersonation_helper";
import { FEEDING_INFO_URLS } from "helpers/feeding_urls";
import { useContext, useEffect, useState } from "react";
import { Col, Row } from "reactstrap";
import { RiRestaurantLine, RiScales3Line, RiExchangeLine, RiGroupLine } from "react-icons/ri";
import { FeedAdministrationHistoryEntry } from "common/data_interfaces";
import LoadingAnimation from "../Shared/LoadingAnimation";
import AlertMessage from "../Shared/AlertMesagge";
import StatKpiCard from "../Graphics/StatKpiCard";
import BasicLineChartCard from "../Graphics/BasicLineChartCard";
import DonutChartCard from "../Graphics/DonutChartCard";
import FeedAdministrationsCard from "../Shared/FeedAdministrationsCard";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { DEFAULT_FARM_CONFIG } from "common/configuration_defaults";
import { FarmConfiguration } from "common/data_interfaces";
import { fetchFarmConfig } from "slices/configurations/thunk";

type Stage = 'piglet' | 'sow' | 'nursery' | 'grower' | 'finisher' | 'general';

interface GroupFeedingDetailsProps {
    groupId: string;
    onUpdate?: () => void;
    isGroupSold?: boolean;
    groupStage?: Stage;
}

const API_CATEGORY_KEY_MAP: Record<string, string> = {
    hygiene_cleaning: 'hygiene',
    equipment_tools: 'equipment',
    spare_parts: 'spare',
    office_supplies: 'office',
    others: 'other',
    pre_starter: 'preStarter',
};

const GroupFeedingDetails: React.FC<GroupFeedingDetailsProps> = ({ groupId, onUpdate, isGroupSold = false, groupStage }) => {
    const { t } = useTranslation();
    const configContext = useContext(ConfigContext);
    const userLogged = getEffectiveUser();
    const farmId = userLogged?.farm_assigned;
    const dispatch: any = useDispatch();
    const farmConfig: FarmConfiguration | null = useSelector((s: any) => s.Configurations.farmConfig);
    const [loading, setLoading] = useState<boolean>(true);
    const [alertConfig, setAlertConfig] = useState({ visible: false, color: "", message: "" });
    const [administrations, setAdministrations] = useState<FeedAdministrationHistoryEntry[]>([]);
    const [feedingStats, setFeedingStats] = useState<any | null>(null);
    const weightUnit = farmConfig?.defaultWeightUnit ?? DEFAULT_FARM_CONFIG.defaultWeightUnit;

    useEffect(() => {
        if (farmId && !farmConfig) {
            dispatch(fetchFarmConfig(farmId));
        }
    }, [dispatch, farmConfig, farmId]);

    const getCategoryLabel = (key: string) =>
        t(`feeding.productCategory.${API_CATEGORY_KEY_MAP[key] ?? key}`, { defaultValue: key });

    const fetchFeedingInfo = async () => {
        if (!configContext || !userLogged) return;
        try {
            setLoading(true);
            const [infoResponse, statsResponse] = await Promise.all([
                configContext.axiosHelper.get(`${configContext.apiUrl}/${FEEDING_INFO_URLS.groupFeedingInfo(groupId)}`),
                configContext.axiosHelper.get(`${configContext.apiUrl}/${FEEDING_INFO_URLS.groupFeedingStats(groupId)}`),
            ]);
            const info = infoResponse.data.data;
            setAdministrations(info?.feedAdministrationHistory ?? []);
            setFeedingStats(statsResponse.data.data);
        } catch (error) {
            logger.error('Error fetching data: ', { error });
            setAlertConfig({ visible: true, color: 'danger', message: t('common.status.noData') });
        } finally {
            setLoading(false);
        }
    };

    const handleAdministered = () => {
        fetchFeedingInfo();
        onUpdate?.();
    };

    useEffect(() => {
        fetchFeedingInfo();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [groupId]);

    if (loading) return <LoadingAnimation />;

    const translatedDist = (feedingStats?.distributionByType || []).map((d: any) => {
        const label = getCategoryLabel(d.id || d.label);
        return { ...d, id: label, label };
    });

    return (
        <>
            {feedingStats && (
                <>
                    <Row className="g-3 mb-3">
                        <Col md={6} lg>
                            <StatKpiCard
                                title={t('feeding.groupFeeding.kpi.consumed')}
                                value={feedingStats.kpis?.totalConsumed || 0}
                                suffix={weightUnit}
                                icon={<RiRestaurantLine size={20} style={{ color: '#f59e0b' }} />}
                                animateValue={true}
                                decimals={1}
                            />
                        </Col>
                        <Col md={6} lg>
                            <StatKpiCard
                                title={t('feeding.groupFeeding.kpi.dailyAvg')}
                                value={feedingStats.kpis?.avgPerDay || 0}
                                suffix={t('feeding.groupFeeding.kpi.weightPerDay', { unit: weightUnit })}
                                icon={<RiScales3Line size={20} style={{ color: '#0ea5e9' }} />}
                                animateValue={true}
                                decimals={2}
                            />
                        </Col>
                        <Col md={6} lg>
                            <StatKpiCard
                                title={t('feeding.groupFeeding.kpi.avgPerPig')}
                                value={feedingStats.kpis?.avgPerPig || 0}
                                suffix={t('feeding.groupFeeding.kpi.weightPerDay', { unit: weightUnit })}
                                icon={<RiGroupLine size={20} style={{ color: '#8b5cf6' }} />}
                                animateValue={true}
                                decimals={2}
                            />
                        </Col>
                        <Col md={6} lg>
                            <StatKpiCard
                                title={t('feeding.groupFeeding.kpi.fcr')}
                                value={feedingStats.kpis?.fcr || feedingStats.kpis?.feedConversionRatio || 0}
                                suffix={t('feeding.groupFeeding.kpi.weightRatio', { unit: weightUnit })}
                                icon={<RiExchangeLine size={20} style={{ color: '#ef4444' }} />}
                                animateValue={true}
                                decimals={2}
                            />
                        </Col>
                    </Row>

                    <Row className="g-3 mb-3">
                        <Col lg={8}>
                            <BasicLineChartCard
                                title={t('feeding.groupFeeding.chart.cumulativeTitle')}
                                data={[{
                                    id: t('feeding.groupFeeding.chart.feedLegend', { unit: weightUnit }),
                                    color: '#f59e0b',
                                    data: (feedingStats.cumulativeConsumption || []).map((p: any) => ({ x: p.date, y: p.value })),
                                }]}
                                yLabel={t('feeding.groupFeeding.chart.yLabel', { unit: weightUnit })}
                                xLabel={t('common.field.date')}
                                height={280}
                                curve="natural"
                                pointSize={5}
                                strokeWidth={2}
                                enableGrid={true}
                                enablePoints={true}
                                enableArea={true}
                                showLegend={false}
                                headerBgColor="#ffffff"
                                className="border-0 shadow-sm h-100"
                            />
                        </Col>
                        <Col lg={4}>
                            <DonutChartCard
                                title={t('feeding.groupFeeding.chart.distributionTitle')}
                                data={translatedDist}
                                legendItems={translatedDist.map((d: any) => ({
                                    label: d.label,
                                    value: `${d.value} ${weightUnit}`,
                                    percentage: feedingStats.kpis?.totalConsumed
                                        ? `${((d.value / feedingStats.kpis.totalConsumed) * 100).toFixed(1)}%`
                                        : '0%',
                                }))}
                                className="h-100 border-0 shadow-sm"
                                headerBgColor="#ffffff"
                            />
                        </Col>
                    </Row>
                </>
            )}

            <div style={{ minHeight: "400px" }}>
                <FeedAdministrationsCard
                    administrations={administrations}
                    targetType="group"
                    targetId={groupId}
                    targetStage={groupStage}
                    defaultWeightUnit={weightUnit}
                    onAdministered={handleAdministered}
                    disabled={isGroupSold}
                />
            </div>

            <AlertMessage
                color={alertConfig.color}
                message={alertConfig.message}
                visible={alertConfig.visible}
                onClose={() => setAlertConfig({ ...alertConfig, visible: false })}
            />
        </>
    );
};

export default GroupFeedingDetails;
